<template>
  <div class="rectification-page">
    <header class="page-head">
      <div>
        <h2>整改事项</h2>
        <p>{{ pendingCount }} 项未完成</p>
      </div>
      <button v-if="canEdit" class="primary-button" type="button" @click="composerOpen = true">＋ 添加整改意见</button>
    </header>

    <section class="list-panel">
      <div class="list-toolbar">
        <div class="status-tabs" role="tablist" aria-label="整改状态">
          <button type="button" :class="{ active: statusFilter === 'ALL' }" @click="statusFilter = 'ALL'">全部 {{ issueTotal }}</button>
          <button type="button" :class="{ active: statusFilter === 'PENDING' }" @click="statusFilter = 'PENDING'">未完成 {{ pendingCount }}</button>
          <button type="button" :class="{ active: statusFilter === 'COMPLETED' }" @click="statusFilter = 'COMPLETED'">已完成 {{ completedCount }}</button>
          <button type="button" :class="{ active: statusFilter === 'VOID' }" @click="statusFilter = 'VOID'">已作废 {{ voidCount }}</button>
        </div>
        <button class="refresh-button" type="button" title="刷新" aria-label="刷新" :disabled="loading" @click="loadBatches">↻</button>
      </div>

      <div v-if="loading" class="page-state">正在读取...</div>
      <div v-else-if="batches.length" class="batch-list">
        <article v-for="batch in visibleBatches" :key="batch.id" class="batch-card">
          <header class="batch-head">
            <div>
              <strong>{{ batch.completedCount }}/{{ batch.issueCount }} 已完成</strong>
              <small>{{ batch.creatorDisplayName || "内部成员" }} · {{ formatTime(batch.createdAt) }}</small>
            </div>
            <button class="source-button" type="button" @click="toggleSource(batch.id)">
              {{ expandedSources[batch.id] ? "收起原问题" : "查看原问题" }}
            </button>
          </header>
          <p v-if="expandedSources[batch.id]" class="source-text">{{ batch.sourceText }}</p>
          <div v-if="batch.loading" class="batch-loading">正在读取事项...</div>
          <div v-else class="issue-list">
            <section v-for="group in groupedItems(batch)" :key="group.name" class="interface-group">
              <header class="interface-head">
                <strong>{{ group.name }}</strong>
                <small>{{ group.completedCount }}/{{ group.items.length }} 已完成</small>
              </header>
              <div v-for="issue in group.items" :key="issue.id" class="issue-row" :class="{ done: isCompleted(issue), voided: isVoided(issue) }">
                <input
                  type="checkbox"
                  :checked="isCompleted(issue)"
                  :disabled="!canComplete || completingId === issue.id || isVoided(issue)"
                  @change="toggleCompletion(issue, $event.target.checked)"
                />
                <span class="issue-content">{{ issue.issueContent }}</span>
                <span class="issue-meta">
                  <small v-if="isCompleted(issue)">{{ issue.completedByDisplayName || "内部成员" }} · {{ formatTime(issue.completedAt) }}</small>
                  <small v-else-if="isVoided(issue)">已作废</small>
                  <small v-else>未完成</small>
                  <button
                    v-if="canComplete"
                    class="issue-action"
                    type="button"
                    :disabled="completingId === issue.id"
                    @click="toggleVoid(issue)"
                  >{{ isVoided(issue) ? "恢复" : "作废" }}</button>
                </span>
              </div>
            </section>
            <div v-if="!filteredItems(batch).length" class="empty-filter">当前没有符合条件的事项</div>
          </div>
        </article>
      </div>
      <div v-else class="page-state page-state--empty">暂无整改事项</div>
    </section>

    <div v-if="composerOpen" class="modal-backdrop" @click.self="composerOpen = false">
      <section class="composer-dialog" role="dialog" aria-modal="true" aria-label="添加整改意见">
        <header class="composer-dialog__head">
          <h3>添加整改意见</h3>
          <button type="button" title="关闭" aria-label="关闭" @click="composerOpen = false">×</button>
        </header>
        <textarea
          v-model.trim="problemText"
          maxlength="5000"
          placeholder="填写原始问题后可使用 AI 整理（可选）"
          aria-label="需要整改的问题"
        ></textarea>
        <div class="composer-actions">
          <span>{{ problemText.length }}/5000</span>
          <span class="composer-actions__buttons">
            <button class="plain-button" type="button" :disabled="draftItems.length >= 50" @click="addDraftItem">直接添加事项</button>
            <button class="primary-button" type="button" :disabled="drafting || !problemText" @click="generateDraft">
              {{ drafting ? "AI 整理中..." : "AI 整理事项" }}
            </button>
          </span>
        </div>

        <div v-if="draftItems.length" class="draft-list">
          <div class="draft-list__head">
            <strong>待保存事项</strong>
            <button class="text-button" type="button" title="添加事项" aria-label="添加事项" @click="addDraftItem">＋</button>
          </div>
          <div v-for="(item, index) in draftItems" :key="item.key" class="draft-row">
            <span>{{ index + 1 }}</span>
            <input v-model.trim="item.interfaceName" maxlength="96" placeholder="所属界面" :aria-label="`第 ${index + 1} 条事项所属界面`" />
            <textarea v-model.trim="item.content" rows="2" maxlength="500" :aria-label="`第 ${index + 1} 条整改事项`"></textarea>
            <button type="button" title="删除事项" aria-label="删除事项" @click="removeDraftItem(index)">×</button>
          </div>
          <div class="draft-list__foot">
            <span>{{ validDraftCount }} 项可保存</span>
            <button class="primary-button" type="button" :disabled="saving || !validDraftCount" @click="saveDraft">
              {{ saving ? "保存中..." : "保存事项" }}
            </button>
          </div>
        </div>
      </section>
    </div>
    <div v-if="toast" class="toast" :class="{ 'toast--error': toastError }" role="status">{{ toast }}</div>
  </div>
</template>

<script>
import { hasAdminPermission } from "../utils/adminSession"
import {
  fetchRectificationBatches,
  fetchRectificationItems,
  generateRectificationDraft,
  saveRectificationBatch,
  setRectificationCompleted,
  setRectificationStatus
} from "../api/officeRectification"

export default {
  name: "OfficeRectificationView",
  data() {
    return {
      problemText: "",
      draftItems: [],
      composerOpen: false,
      batches: [],
      expandedSources: {},
      statusFilter: "ALL",
      loading: false,
      drafting: false,
      saving: false,
      completingId: null,
      draftKey: 0,
      toast: "",
      toastError: false,
      toastTimer: null
    }
  },
  computed: {
    canEdit() {
      return hasAdminPermission("office:rectification:edit")
    },
    canComplete() {
      return hasAdminPermission("office:rectification:complete")
    },
    validDraftCount() {
      return this.draftItems.filter(item => item.content.trim()).length
    },
    issueTotal() {
      return this.batches.reduce((total, batch) => total + Number(batch.issueCount || 0), 0)
    },
    completedCount() {
      return this.batches.reduce((total, batch) => total + Number(batch.completedCount || 0), 0)
    },
    voidCount() {
      return this.batches.reduce((total, batch) => total + (batch.items || []).filter(item => this.isVoided(item)).length, 0)
    },
    pendingCount() {
      return this.issueTotal - this.completedCount - this.voidCount
    },
    visibleBatches() {
      return this.batches.filter(batch => this.statusFilter === "ALL" || this.filteredItems(batch).length)
    }
  },
  created() {
    this.loadBatches()
  },
  methods: {
    async loadBatches() {
      this.loading = true
      try {
        const response = await fetchRectificationBatches()
        const batches = Array.isArray(response.data) ? response.data : []
        this.batches = batches.map(batch => ({ ...batch, items: [], loading: true }))
        await Promise.all(this.batches.map(batch => this.loadItems(batch)))
      } catch (error) {
        this.showToast(error.message || "整改事项读取失败", true)
      } finally {
        this.loading = false
      }
    },
    async loadItems(batch) {
      try {
        const response = await fetchRectificationItems(batch.id)
        batch.items = Array.isArray(response.data) ? response.data : []
      } catch (error) {
        batch.items = []
        this.showToast(error.message || "整改事项读取失败", true)
      } finally {
        batch.loading = false
      }
    },
    async generateDraft() {
      if (!this.problemText) return
      this.drafting = true
      try {
        const response = await generateRectificationDraft({ problemText: this.problemText })
        const items = response.data && Array.isArray(response.data.items) ? response.data.items : []
        this.draftItems = items.map(item => typeof item === "string"
          ? this.newDraftItem("未分类界面", item)
          : this.newDraftItem(item.interfaceName, item.content))
        this.showToast(response.message || "AI 已整理事项")
      } catch (error) {
        this.showToast(error.message || "AI 整理失败", true)
      } finally {
        this.drafting = false
      }
    },
    newDraftItem(interfaceName = "", content = "") {
      this.draftKey += 1
      return { key: this.draftKey, interfaceName: interfaceName || "", content: content || "" }
    },
    addDraftItem() {
      if (this.draftItems.length < 50) this.draftItems.push(this.newDraftItem())
    },
    removeDraftItem(index) {
      this.draftItems.splice(index, 1)
    },
    async saveDraft() {
      const items = this.draftItems
        .filter(item => item.content.trim())
        .map(item => ({ interfaceName: item.interfaceName.trim() || "未分类界面", content: item.content.trim() }))
      if (!items.length) return
      this.saving = true
      try {
        const fallbackText = items.map(item => `${item.interfaceName}：${item.content}`).join("\n")
        await saveRectificationBatch({ problemText: this.problemText || fallbackText, items })
        this.problemText = ""
        this.draftItems = []
        this.composerOpen = false
        this.showToast("整改事项已保存")
        await this.loadBatches()
      } catch (error) {
        this.showToast(error.message || "整改事项保存失败", true)
      } finally {
        this.saving = false
      }
    },
    filteredItems(batch) {
      const items = Array.isArray(batch.items) ? batch.items : []
      return this.statusFilter === "ALL" ? items : items.filter(item => item.status === this.statusFilter)
    },
    groupedItems(batch) {
      const groups = new Map()
      this.filteredItems(batch).forEach(item => {
        const name = item.interfaceName || "未分类界面"
        if (!groups.has(name)) groups.set(name, [])
        groups.get(name).push(item)
      })
      return Array.from(groups, ([name, items]) => ({
        name,
        items,
        completedCount: items.filter(item => this.isCompleted(item)).length
      }))
    },
    isCompleted(issue) {
      return issue.status === "COMPLETED"
    },
    isVoided(issue) {
      return issue.status === "VOID"
    },
    async toggleCompletion(issue, completed) {
      await this.updateIssueStatus(issue, completed ? "COMPLETED" : "PENDING")
    },
    async toggleVoid(issue) {
      await this.updateIssueStatus(issue, this.isVoided(issue) ? "PENDING" : "VOID")
    },
    async updateIssueStatus(issue, status) {
      this.completingId = issue.id
      try {
        const response = status === "COMPLETED" || status === "PENDING"
          ? await setRectificationCompleted(issue.id, status === "COMPLETED")
          : await setRectificationStatus(issue.id, status)
        const updated = response.data
        this.batches.forEach(batch => {
          const index = batch.items.findIndex(item => item.id === issue.id)
          if (index >= 0) {
            this.$set(batch.items, index, updated)
            const completedDelta = (updated.status === "COMPLETED" ? 1 : 0) - (issue.status === "COMPLETED" ? 1 : 0)
            batch.completedCount = Number(batch.completedCount || 0) + completedDelta
          }
        })
      } catch (error) {
        this.showToast(error.message || "状态更新失败", true)
      } finally {
        this.completingId = null
      }
    },
    toggleSource(batchId) {
      this.$set(this.expandedSources, batchId, !this.expandedSources[batchId])
    },
    showToast(message, error = false) {
      clearTimeout(this.toastTimer)
      this.toast = message
      this.toastError = error
      this.toastTimer = setTimeout(() => { this.toast = "" }, 3000)
    },
    formatTime(value) {
      if (!value) return ""
      const date = new Date(String(value).replace(/-/g, "/"))
      if (Number.isNaN(date.getTime())) return value
      const pad = number => String(number).padStart(2, "0")
      return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())} ${pad(date.getHours())}:${pad(date.getMinutes())}`
    }
  }
}
</script>

<style scoped>
.rectification-page { max-width: 1080px; margin: 0 auto; }
.page-head, .list-toolbar, .composer-actions, .draft-list__head, .draft-list__foot, .batch-head { display: flex; align-items: center; justify-content: space-between; gap: 16px; }
.page-head { margin-bottom: 18px; }
.page-head h2 { margin: 0; color: #1f2937; font-size: 22px; }
.page-head p { margin: 6px 0 0; color: #667085; font-size: 13px; }
.list-panel { border: 1px solid #e3e8ef; background: #fff; border-radius: 8px; }
.modal-backdrop { position: fixed; inset: 0; z-index: 90; display: grid; place-items: center; padding: 20px; background: rgba(22, 29, 36, .42); }
.composer-dialog { width: min(760px, 100%); max-height: calc(100vh - 40px); overflow-y: auto; padding: 20px; border-radius: 8px; background: #fff; box-shadow: 0 22px 60px rgba(12, 18, 24, .2); }
.composer-dialog__head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.composer-dialog__head h3 { margin: 0; color: #1f2937; font-size: 17px; }
.composer-dialog__head button { border: 0; background: transparent; color: #68747f; font-size: 22px; cursor: pointer; }
.composer-dialog textarea { display: block; box-sizing: border-box; width: 100%; min-height: 154px; padding: 12px; resize: vertical; border: 1px solid #d8e0ea; border-radius: 6px; color: #263238; font: inherit; line-height: 1.65; outline: none; }
.composer-dialog textarea:focus, .draft-row textarea:focus, .draft-row input:focus { border-color: #2563eb; box-shadow: 0 0 0 3px #dbeafe; }
.composer-actions { margin-top: 10px; }
.composer-actions__buttons { display: flex; gap: 8px; }
.composer-actions > span, .draft-list__foot > span { color: #8a96a8; font-size: 12px; }
.primary-button { min-height: 34px; padding: 0 14px; border: 0; border-radius: 6px; background: #2563eb; color: #fff; cursor: pointer; font: inherit; }
.plain-button { min-height: 34px; padding: 0 12px; border: 1px solid #d3dae0; border-radius: 6px; background: #fff; color: #53606c; cursor: pointer; font: inherit; }
.primary-button:disabled, .plain-button:disabled, .refresh-button:disabled, .issue-action:disabled { cursor: not-allowed; opacity: .55; }
.draft-list { margin-top: 16px; border-top: 1px solid #edf1f5; }
.draft-list__head { padding: 14px 0 8px; color: #344054; font-size: 14px; }
.text-button, .draft-row button, .refresh-button, .source-button { border: 0; background: transparent; color: #2563eb; cursor: pointer; font: inherit; }
.text-button { font-size: 20px; line-height: 20px; }
.draft-row { display: grid; grid-template-columns: 24px 150px minmax(0, 1fr) 28px; gap: 8px; align-items: start; margin-top: 8px; }
.draft-row > span { color: #8a96a8; font-size: 13px; text-align: center; }
.draft-row textarea, .draft-row input { width: 100%; min-width: 0; box-sizing: border-box; padding: 7px 9px; border: 1px solid #d8e0ea; border-radius: 5px; color: #263238; font: inherit; line-height: 1.5; outline: none; }
.draft-row textarea { min-height: 52px; resize: vertical; }
.draft-row input { height: 38px; }
.draft-row button { color: #98a2b3; font-size: 20px; }
.draft-list__foot { margin-top: 12px; }
.list-panel { overflow: hidden; }
.list-toolbar { min-height: 56px; padding: 0 14px; border-bottom: 1px solid #edf1f5; }
.status-tabs { display: flex; gap: 2px; }
.status-tabs button { min-height: 32px; padding: 0 10px; border: 0; border-radius: 5px; background: transparent; color: #667085; cursor: pointer; font: inherit; font-size: 13px; }
.status-tabs button.active { background: #eaf2ff; color: #1d4ed8; font-weight: 600; }
.refresh-button { width: 32px; height: 32px; border: 1px solid #d8e0ea; border-radius: 5px; color: #667085; font-size: 18px; }
.batch-list { padding: 14px; }
.batch-card { border: 1px solid #e5eaf0; border-radius: 7px; overflow: hidden; }
.batch-card + .batch-card { margin-top: 12px; }
.batch-head { min-height: 52px; padding: 0 14px; background: #fbfcfe; }
.batch-head strong { display: block; color: #344054; font-size: 14px; }
.batch-head small { display: block; margin-top: 3px; color: #98a2b3; font-size: 12px; }
.source-button { font-size: 12px; white-space: nowrap; }
.source-text { margin: 0; padding: 10px 14px; border-top: 1px solid #edf1f5; background: #f8fafc; color: #667085; white-space: pre-wrap; font-size: 13px; line-height: 1.6; }
.issue-list { padding: 0 14px 4px; }
.interface-group + .interface-group { border-top: 1px solid #e5eaf0; }
.interface-head { display: flex; align-items: center; justify-content: space-between; gap: 12px; padding: 14px 0 5px; }
.interface-head strong { color: #344054; font-size: 14px; }
.interface-head small { color: #98a2b3; font-size: 12px; }
.issue-row { display: grid; grid-template-columns: 18px minmax(0, 1fr) auto; gap: 10px; align-items: center; min-height: 48px; border-bottom: 1px solid #f0f3f6; }
.issue-row:last-child { border-bottom: 0; }
.issue-row input { width: 16px; height: 16px; accent-color: #2563eb; cursor: pointer; }
.issue-content { color: #344054; font-size: 14px; line-height: 1.45; }
.issue-row.done .issue-content { color: #98a2b3; text-decoration: line-through; }
.issue-row.voided .issue-content { color: #98a2b3; text-decoration: line-through; }
.issue-row small { color: #98a2b3; font-size: 12px; white-space: nowrap; }
.issue-meta { display: flex; align-items: center; justify-content: flex-end; gap: 10px; }
.issue-action { border: 0; background: transparent; color: #a64438; cursor: pointer; font: inherit; font-size: 12px; }
.voided .issue-action { color: #2563eb; }
.batch-loading, .empty-filter, .page-state { padding: 34px 16px; color: #98a2b3; text-align: center; font-size: 13px; }
.page-state--empty { padding: 68px 16px; }
.toast { position: fixed; right: 24px; bottom: 24px; z-index: 120; max-width: 340px; padding: 11px 14px; border-radius: 6px; background: #274b3f; color: #fff; font-size: 12px; box-shadow: 0 10px 26px rgba(20, 37, 31, .2); }
.toast--error { background: #a64438; }
@media (max-width: 640px) {
  .rectification-page { margin: 0; }
  .composer-dialog { width: 100%; max-height: 100vh; padding: 16px; border-radius: 0; }
  .modal-backdrop { padding: 0; }
  .batch-list { padding: 10px; }
  .issue-row { grid-template-columns: 18px minmax(0, 1fr); padding: 10px 0; }
  .issue-meta { grid-column: 2; justify-content: flex-start; }
  .status-tabs button { padding: 0 7px; font-size: 12px; }
  .composer-actions { align-items: flex-end; }
  .composer-actions__buttons { flex-wrap: wrap; justify-content: flex-end; }
  .draft-row { grid-template-columns: 24px minmax(0, 1fr) 28px; }
  .draft-row input, .draft-row textarea { grid-column: 2; }
  .draft-row textarea { grid-row: 2; }
  .draft-row button { grid-column: 3; grid-row: 1; }
}
</style>
